import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {SUN_STARS_KIND} from '../../../configs/biomeEffects/sunStars';
import windGust from './windGust';
import {
  lerp,
  pickPointInOval,
  pickWeighted,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * СОЛНЕЧНЫЕ ЗВЕЗДЫ НА ВОДЕ (островная подложка). Чистые функции без Phaser и
 * DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Вспышка живет на одном из кругов воды, сверенных с картинкой пейзажа:
 *  - звезда: яркое ядро с четырьмя тонкими лучами. Раскрывается, чуть
 *    поворачиваясь, и сворачивается;
 *  - отсвет: мягкое голубоватое пятно под звездой;
 *  - спутники: одна-три искры поменьше на том же гребне волны — в стороне от
 *    звезды по горизонтали, загораются одна за другой.
 *
 * Все частицы светятся (накладываются сложением) и лежат на самой земле: жетон
 * у кромки поля звезду закроет. Над игровыми ячейками звезды не рождаются.
 */

const STAR = 'star';
const HALO = 'halo';
const COMPANION = 'companion';

// Сколько раз вспышка ищет в своем круге воды место не над полем
const BIRTH_ATTEMPTS = 8;
// По скольким точкам круга воды оценивается, какая его часть свободна от поля
const OPEN_SHARE_PROBES = 24;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** Точка, стянутая внутрь овала: дальше его кромки (с запасом share) не выходит */
const pullIntoOval = (oval, x, y, share) => {
  const distance = Math.hypot((x - oval.x) / oval.radiusX, (y - oval.y) / oval.radiusY);
  if (!(distance > share)) {
    return {x, y};
  }
  return {x: oval.x + ((x - oval.x) * share) / distance, y: oval.y + ((y - oval.y) * share) / distance};
};

const createTracks = (context, params) => {
  const {hexSize, side, cells} = context;
  const keepOff = hexSize * params.keepOffCellFactor;
  const minSize = hexSize * params.star.sizeFactor.min;
  const maxSize = hexSize * params.star.sizeFactor.max;

  /** Лежит ли точка над игровой ячейкой или вплотную к ней */
  const checkIfOverField = (water, x, y) => (
    water.nearCells.some((cell) => Math.hypot(cell.x - x, cell.y - y) < keepOff)
  );

  // Круги воды в координатах мира. У каждого — ячейки поля, которые могут
  // оказаться под звездой (обычно их нет вовсе), и вес: круг, наполовину
  // занятый полем, вспыхивает вдвое реже, а занятый целиком — никогда
  const waters = params.waters.map(({u, v, radius, weight}, index) => {
    const oval = context.toWorldOval({u, v, radiusU: radius});
    const water = {
      index,
      oval,
      nearCells: cells.filter((cell) => (
        Math.abs(cell.x - oval.x) < oval.radiusX + keepOff && Math.abs(cell.y - oval.y) < oval.radiusY + keepOff
      )),
      weight,
    };
    if (water.nearCells.length > 0) {
      let openCount = 0;
      for (let probe = 0; probe < OPEN_SHARE_PROBES; probe += 1) {
        const distance = Math.sqrt((probe + 0.5) / OPEN_SHARE_PROBES) * params.waterInsetShare;
        const x = oval.x + Math.cos(probe * GOLDEN_ANGLE) * oval.radiusX * distance;
        const y = oval.y + Math.sin(probe * GOLDEN_ANGLE) * oval.radiusY * distance;
        openCount += checkIfOverField(water, x, y) ? 0 : 1;
      }
      water.weight = (weight * openCount) / OPEN_SHARE_PROBES;
    }
    return water;
  });

  const buildGroup = ({rng, liveGroups}) => {
    // На одном месте воды — одна вспышка разом
    const busyIndexes = new Set(liveGroups.map((group) => group.waterIndex));
    const water = pickWeighted(rng, waters, (candidate) => (busyIndexes.has(candidate.index) ? 0 : candidate.weight));
    if (!water) {
      return null;
    }
    let place = null;
    for (let attempt = 0; attempt < BIRTH_ATTEMPTS && !place; attempt += 1) {
      const candidate = pickPointInOval(rng, water.oval, params.waterInsetShare);
      place = checkIfOverField(water, candidate.x, candidate.y) ? null : candidate;
    }
    if (!place) {
      return null;
    }

    const size = Math.min(maxSize, Math.max(minSize, side * randomIn(rng, params.star.sizeShare)));
    const lifeMs = randomIn(rng, params.flash.lifeMs);
    const turnRad = toRadians(params.flash.turnDeg) * randomSign(rng);
    const glow = {layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW, x: place.x, y: place.y, delayMs: 0, lifeMs};
    const particles = [
      // Отсвет — под звездой
      {
        ...glow, kind: HALO, sublayer: 0, texture: FIELD_BIOME_TEXTURES.DOT, tint: params.halo.tint,
        size: size * params.halo.scale, alpha: params.halo.alpha, turnRad: 0,
      },
      {
        ...glow, kind: STAR, sublayer: 1, texture: FIELD_BIOME_TEXTURES.SPARK, tint: params.star.tint,
        size, alpha: params.star.alpha, turnRad,
      },
    ];

    let durationMs = lifeMs;
    let delayMs = 0;
    const companionCount = randomInt(rng, params.companions.count.min, params.companions.count.max);
    for (let index = 0; index < companionCount; index += 1) {
      delayMs += randomIn(rng, params.companions.staggerMs);
      // Спутник — на том же гребне волны: в стороне по горизонтали, чуть выше
      // или ниже. Из круга воды не выходит
      const spot = pullIntoOval(
        water.oval,
        place.x + randomSign(rng) * hexSize * randomIn(rng, params.companions.alongFactor),
        place.y + hexSize * randomRange(rng, -params.companions.acrossFactor, params.companions.acrossFactor),
        1
      );
      if (!checkIfOverField(water, spot.x, spot.y)) {
        const companionLifeMs = lifeMs * params.companions.lifeShare;
        particles.push({
          kind: COMPANION, layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW, sublayer: 1, texture: FIELD_BIOME_TEXTURES.SPARK,
          tint: params.star.tint, x: spot.x, y: spot.y, delayMs, lifeMs: companionLifeMs,
          size: size * params.companions.scale, alpha: params.companions.alpha, turnRad,
        });
        durationMs = Math.max(durationMs, delayMs + companionLifeMs);
      }
    }

    return {durationMs, waterIndex: water.index, particles};
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const ageMs = elapsedMs - particle.delayMs;
    if (!(ageMs > 0) || !(ageMs < particle.lifeMs)) {
      return writeHiddenPose(out);
    }
    // Вспышка: размер от малого к полному и обратно, яркость — от нуля
    const swell = Math.sin((Math.PI * ageMs) / particle.lifeMs);
    const size = particle.size * lerp(params.flash.minScale, 1, swell);
    return writePose(
      out, particle.x, particle.y, particle.alpha * swell * swell, size, size,
      particle.turnRad * (ageMs / particle.lifeMs)
    );
  };

  const starsTrack = {
    // Звездам ветер матча не нужен: при его смене дорожка идет как шла
    usesWind: false,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups},
    buildGroup,
    resolvePose,
  };
  // Морской бриз по траве поля: построитель порыва ветра над лугом со своими
  // числами. Звезды лежат на воде за полем, а бриз проходит там, куда смотрит камера
  return [starsTrack, ...windGust.createTracks(context, params.breeze)];
};

const sunStars = {
  kind: SUN_STARS_KIND,
  textures: [...new Set([FIELD_BIOME_TEXTURES.DOT, FIELD_BIOME_TEXTURES.SPARK, ...windGust.textures])],
  createTracks,
};

export default sunStars;
